import type { DisplayFilter, Range, Scale, View, ViewConfig } from './types';
import { getDefaultRangeForView, getValidRangesForView } from './types';
import { getCurrentMonthKey, getCurrentWeekKey } from './utils';

/**
 * URL building for the Timeline's view / scale / range / display controls.
 * Shared by the desktop `Header` and the mobile Timeline header + View options
 * sheet, so both produce the same URL for the same choice.
 */

/** Where the Timeline currently is (the Dashboard's props). */
export interface TimelineState {
  scale: Scale;
  view: View;
  range: Range;
  display: DisplayFilter | null;
  currentMonth: string;
  currentWeek: string;
  currentYear: number;
  viewConfig: ViewConfig;
}

const SCALE_SEARCH_ORDER: Scale[] = ['week', 'month', 'year'];

/**
 * Ranges offered for a view at a scale. The "days" view always uses week-style
 * ranges, whatever the scale.
 */
export function getRangeOptionsForView(view: View, scale: Scale, viewConfig: ViewConfig): string[] {
  return view === 'days'
    ? getValidRangesForView('days', 'week', viewConfig)
    : getValidRangesForView(view, scale, viewConfig);
}

/** The Timeline URL for a scale/view/range/display, omitting default params. */
export function buildTimelineUrl(
  state: TimelineState,
  newScale: Scale,
  newView: View,
  newRange: Range,
  timeKeyOverride?: string,
  newDisplay?: DisplayFilter | null,
): string {
  const { viewConfig } = state;
  const params = new URLSearchParams();

  // Always include view param (except for default views)
  const defaultView = viewConfig.defaultViewForScale[newScale];
  if (newView !== defaultView) {
    params.set('view', newView);
  }

  // Include range if scale supports it and it's not the default
  // "days" view always uses week-style ranges regardless of scale
  const effectiveScale = newView === 'days' ? 'week' : newScale;
  const viewRanges = getValidRangesForView(newView, effectiveScale, viewConfig);
  const defaultRange = getDefaultRangeForView(newView, effectiveScale, viewConfig);
  if (viewRanges.length > 0 && newRange && newRange !== defaultRange) {
    params.set('range', newRange);
  }

  // Include display if it's not the default for the view
  const displayToUse = newDisplay !== undefined ? newDisplay : state.display;
  const defaultDisplay = viewConfig.defaultDisplay[newView];
  if (displayToUse && defaultDisplay && displayToUse !== defaultDisplay) {
    params.set('display', displayToUse);
  }

  const query = params.toString();
  const queryStr = query ? `?${query}` : '';

  switch (newScale) {
    case 'year':
      return `/year/${timeKeyOverride || state.currentYear}${queryStr}`;
    case 'week':
      return `/week/${timeKeyOverride || state.currentWeek}${queryStr}`;
    default:
      return `/month/${timeKeyOverride || state.currentMonth}${queryStr}`;
  }
}

/**
 * URL for switching to another view. Keeps the scale when the view supports it,
 * otherwise moves to the first scale that does (at the current period of that
 * scale). Resets range and display to the view's defaults. `null` for the current view.
 */
export function timelineUrlForView(state: TimelineState, newView: View): string | null {
  const { scale, view, viewConfig } = state;
  if (newView === view) return null;

  // If new view isn't valid for current scale, find a valid scale
  let targetScale = scale;
  if (!viewConfig.viewsByScale[scale]?.includes(newView)) {
    for (const s of SCALE_SEARCH_ORDER) {
      if (viewConfig.viewsByScale[s]?.includes(newView)) {
        targetScale = s;
        break;
      }
    }
  }

  // Use the appropriate time key for the target scale
  let timeKey: string | undefined;
  if (targetScale !== scale) {
    switch (targetScale) {
      case 'year':
        timeKey = String(state.currentYear);
        break;
      case 'week':
        timeKey = getCurrentWeekKey();
        break;
      default:
        timeKey = getCurrentMonthKey();
        break;
    }
  }

  // Reset range and display when changing views (use view-specific default if available)
  const effectiveScale = newView === 'days' ? 'week' : targetScale;
  const targetRange = (getDefaultRangeForView(newView, effectiveScale, viewConfig) ??
    viewConfig.defaultRanges[targetScale]) as Range;
  const targetDisplay = (viewConfig.defaultDisplay[newView] as DisplayFilter) || null;
  return buildTimelineUrl(state, targetScale, newView, targetRange, timeKey, targetDisplay);
}

/**
 * URL for switching scale. Keeps the view when the scale supports it (else the
 * scale's default view) and jumps to today's period. `null` for the current scale.
 */
export function timelineUrlForScale(state: TimelineState, newScale: Scale): string | null {
  const { scale, view, range, viewConfig } = state;
  if (newScale === scale) return null;

  // Get valid view for new scale (keep current if valid, otherwise use default)
  const validViews = viewConfig.viewsByScale[newScale] || [];
  const newView = validViews.includes(view)
    ? view
    : (viewConfig.defaultViewForScale[newScale] as View);

  // The "days" view uses week-style ranges regardless of scale, so preserve the current range
  // Use view-specific default range if available
  const effectiveScaleForRange = newView === 'days' ? 'week' : newScale;
  const newRange = (
    newView === 'days'
      ? range || viewConfig.defaultRanges['week']
      : (getDefaultRangeForView(newView, effectiveScaleForRange, viewConfig) ??
        viewConfig.defaultRanges[newScale])
  ) as Range;

  // Navigate to "today" for the new scale
  let timeKey: string;
  switch (newScale) {
    case 'year':
      timeKey = String(new Date().getFullYear());
      break;
    case 'week':
      timeKey = getCurrentWeekKey();
      break;
    default:
      timeKey = getCurrentMonthKey();
      break;
  }

  return buildTimelineUrl(state, newScale, newView, newRange, timeKey);
}

/** URL for another range of the current view. `null` for the current range. */
export function timelineUrlForRange(state: TimelineState, newRange: string): string | null {
  if (newRange === state.range) return null;
  return buildTimelineUrl(state, state.scale, state.view, newRange as Range);
}

/** URL for another display filter of the current view. `null` for the current one. */
export function timelineUrlForDisplay(
  state: TimelineState,
  newDisplay: DisplayFilter,
): string | null {
  if (newDisplay === state.display) return null;
  return buildTimelineUrl(state, state.scale, state.view, state.range, undefined, newDisplay);
}
